import { useEffect, useState, FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { validatePublicToken, publicCreateSignin } from '../lib/publicSharing';
import { captureGeolocation } from '../lib/geo';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { useDelayedLoading } from '../lib/useDelayedLoading';

const CONFIRMATIONS = [
  "I've been inducted on this site",
  "I'll wear hard hat, boots and hi-vis at all times",
  "I've read my RAMS for today's work and understand it",
  "If I'm not sure something is safe — I'll stop and ask",
];

interface SiteContext {
  orgName: string;
  siteName: string;
  expiresAt: string;
}

/**
 * Public sign-in page. Anyone with the QR / link can sign in without
 * a login. Validated server-side via the validate_public_token RPC.
 */
export function PublicSignIn() {
  const { token } = useParams<{ token: string }>();
  useDocumentTitle(token ? 'Site sign-in' : 'Sign-in link');

  const [ctx, setCtx] = useState<SiteContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<{ name: string } | null>(null);

  const [fullName, setFullName] = useState('');
  const [company, setCompany] = useState('');
  const [trade, setTrade] = useState('');
  const [mobile, setMobile] = useState('');
  const [vehicleReg, setVehicleReg] = useState('');
  const [confirms, setConfirms] = useState<boolean[]>([false, false, false, false]);

  useEffect(() => {
    if (!token) { setError('No token provided.'); setLoading(false); return; }
    validatePublicToken(token, 'site_signin')
      .then((r) => {
        setCtx({ orgName: r.org_name, siteName: r.site_name, expiresAt: r.expires_at });
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    if (!fullName.trim() || !company.trim() || !mobile.trim()) {
      setError('Name, company and mobile are required.'); return;
    }
    if (!confirms.every(Boolean)) {
      setError('Please tick all four confirmations to continue.'); return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const geo = await captureGeolocation(5000).catch(() => null);
      await publicCreateSignin({
        token,
        fullName: fullName.trim(),
        company: company.trim(),
        tradeRole: trade.trim() || null,
        mobile: mobile.trim(),
        vehicleReg: vehicleReg.trim() || null,
        confirmations: confirms,
        lat: geo?.lat ?? null,
        lng: geo?.lng ?? null,
        address: geo?.address ?? null,
        accuracyM: geo?.accuracy_m ?? null,
      });
      setDone({ name: fullName.trim() });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSubmitting(false);
    }
  }

  // Delay the auth-shell loading text — same premium-feel pattern as
  // LoadingIndicator. Most token validations resolve under 500ms.
  const showLoading = useDelayedLoading(loading);
  if (loading) {
    return <div className="auth-shell">{showLoading && <p className="brand-tag">Loading…</p>}</div>;
  }

  if (error && !ctx) {
    return (
      <div className="auth-shell">
        <div className="auth-card" style={{ maxWidth: 420 }}>
          <h2 style={{ marginTop: 0, color: 'var(--danger)' }}>Can't sign in</h2>
          <p>{error}</p>
          <p className="muted" style={{ fontSize: 13 }}>
            Ask a site manager for a current sign-in link or QR code.
          </p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="auth-shell">
        <div className="auth-card" style={{ maxWidth: 420, textAlign: 'center' }}>
          <h2 style={{ marginTop: 0 }}>✓ Signed in</h2>
          <p style={{ marginBottom: 24 }}>
            <strong>{done.name}</strong> — you're checked in for{' '}
            <strong>{ctx?.siteName ?? 'this site'}</strong>.
          </p>
          <p className="muted" style={{ fontSize: 13 }}>
            Site manager: {ctx?.orgName ?? ''}<br />
            You can close this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-shell" style={{ alignItems: 'flex-start', paddingTop: 24, paddingBottom: 48 }}>
      <div className="auth-card" style={{ maxWidth: 480, width: '100%' }}>
        <p className="brand-tag" style={{ margin: 0 }}>{ctx?.orgName}</p>
        <h2 style={{ margin: '4px 0 4px' }}>Sign in to site</h2>
        <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: 14 }}>
          {ctx?.siteName}
        </p>

        {error && <div className="banner banner-error" style={{ marginTop: 12 }}>{error}</div>}

        <form onSubmit={onSubmit} style={{ marginTop: 16 }}>
          <div className="field">
            <label htmlFor="ps-name">Full name *</label>
            <input id="ps-name" type="text" required autoComplete="name" enterKeyHint="next" placeholder="e.g. Mike Doyle" value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="ps-co">Company *</label>
            <input id="ps-co" type="text" required autoComplete="organization" enterKeyHint="next" placeholder="e.g. ACME Bricklaying Ltd" value={company} onChange={(e) => setCompany(e.target.value)} />
          </div>
          <div className="field-row">
            <div className="field">
              <label htmlFor="ps-trade">Trade / role</label>
              <input id="ps-trade" type="text" enterKeyHint="next" placeholder="e.g. Bricklayer" value={trade} onChange={(e) => setTrade(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="ps-mob">Mobile *</label>
              <input id="ps-mob" type="tel" required autoComplete="tel" inputMode="tel" enterKeyHint="next" placeholder="07700 900100" value={mobile} onChange={(e) => setMobile(e.target.value)} />
            </div>
          </div>
          <div className="field">
            <label htmlFor="ps-veh">Vehicle reg (if driving)</label>
            <input id="ps-veh" type="text" enterKeyHint="done" autoCapitalize="characters" placeholder="AB12 CDE — leave blank if none" value={vehicleReg} onChange={(e) => setVehicleReg(e.target.value)} />
          </div>

          <h3 className="section-title" style={{ marginTop: 18 }}>Confirm before signing in</h3>
          <div className="check-list">
            {CONFIRMATIONS.map((label, i) => (
              <label key={i} className="check-row">
                <input
                  type="checkbox"
                  checked={confirms[i]}
                  onChange={(e) => {
                    const next = confirms.slice();
                    next[i] = e.target.checked;
                    setConfirms(next);
                  }}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>

          <div className="btn-stack" style={{ marginTop: 18 }}>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
